import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { api } from '../api/endpoints'
import { NotSignedIn } from '../api/client'
import type { AccountMe } from '../api/types'

type Status = 'loading' | 'signedIn' | 'signedOut'

interface Session {
  status: Status
  account: AccountMe | null
  /** Re-reads the account. Called after signing in and after confirming an address. */
  refresh: () => Promise<void>
  signOut: () => Promise<void>
}

const SessionContext = createContext<Session | null>(null)

/**
 * Who is signed in, asked once and shared.
 *
 * The API answers `GET /api/account/me` with 401 when there is no session, so that single call
 * is both "who is this" and "is anyone here" — there is no token to inspect on the client and
 * deliberately so: 17A keeps the session in an HttpOnly cookie, which JavaScript cannot read.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading')
  const [account, setAccount] = useState<AccountMe | null>(null)

  const refresh = useCallback(async () => {
    try {
      const me = await api.account.me()
      setAccount(me)
      setStatus('signedIn')
    } catch (error) {
      if (error instanceof NotSignedIn) {
        setAccount(null)
        setStatus('signedOut')
        return
      }

      // A network failure is not the same as being signed out, and pretending it is would bounce
      // someone to the sign-in page for a dropped connection.
      setStatus('signedOut')
      throw error
    }
  }, [])

  const signOut = useCallback(async () => {
    try {
      await api.auth.logout()
    } finally {
      setAccount(null)
      setStatus('signedOut')
    }
  }, [])

  useEffect(() => {
    void refresh().catch(() => undefined)
  }, [refresh])

  const value = useMemo<Session>(
    () => ({ status, account, refresh, signOut }),
    [status, account, refresh, signOut],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): Session {
  const value = useContext(SessionContext)

  if (!value) {
    throw new Error('useSession was called outside SessionProvider.')
  }

  return value
}
